import React, { Component } from 'react';
import { FileBrowser } from './FileBrowser';
import { FileViewer } from './FileViewer';
import { Theme } from '../Theme';
import { Button } from './Button';
import { getFunctions, httpsCallable } from 'firebase/functions';

export type FilesAreaProps = {
    healthcareProviderId: string;
};

type State = {
    selectedFile?: {url: string, name: string, path: string};
};

export class FilesArea extends Component<FilesAreaProps, State> {

    constructor(props: FilesAreaProps) {
        super(props);
        this.state = {
            selectedFile: undefined,
        };
    }

    componentDidMount() {
        void this.ensureStorageStructure();
    }

    componentDidUpdate(prevProps: FilesAreaProps) {
        if (prevProps.healthcareProviderId !== this.props.healthcareProviderId) {
            this.setState({ selectedFile: undefined });
            void this.ensureStorageStructure();
        }
    }

    private async ensureStorageStructure() {
        if (!this.props.healthcareProviderId) {
            return;
        }

        try {
            const functions = getFunctions();
            const ensureHealthcareProviderStorageStructure = httpsCallable<
                { providerId: string },
                { ensured: boolean; providerId: string }
            >(functions, 'ensureHealthcareProviderStorageStructure');

            await ensureHealthcareProviderStorageStructure({
                providerId: this.props.healthcareProviderId,
            });
        } catch (error) {
            console.error('FILES_AREA: ensureStorageStructure(): Error ensuring storage structure:', error);
        }
    }

    private renderFileViewer(): React.ReactNode {
        if (!this.state.selectedFile) {
            // "Select a file from the browser to view its details."
            return <div></div>;
        }

        return <FileViewer file={this.state.selectedFile} />;
    };

    private handleFileSelect(fileUrl: string, fileName: string, filePath: string) {
        this.setState({ 
            selectedFile: { url: fileUrl, name: fileName, path: filePath },
        });
    };

    render(): React.ReactNode {
        console.log('FILES_AREA: render() this.props.healthcareProviderId:', this.props.healthcareProviderId);
        return (
            <div style={styles.page}>

                {/* file picker */}
                <div style={styles.leftPanel}>
                    <div style={{ flexGrow: 1, display: 'flex', minHeight: 0, padding: '20px' }}>
                        <div style={{ width: '100%' }}>
                            <FileBrowser
                                basePath={`HealthcareProviders/${this.props.healthcareProviderId}/SharedFiles`}
                                onFileSelect={(fileUrl: any, fileName: any, filePath: any) => {this.handleFileSelect(fileUrl, fileName, filePath);}}
                                onFileDeleted={(filePath: string) => {
                                    if (this.state.selectedFile?.path === filePath) {
                                        this.setState({ selectedFile: undefined });
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* file viewer */}
                {this.state.selectedFile && (
                    <div style={styles.contentPanel}>
                        <div style={styles.filePathContainer}>
                            <div style={styles.filePath}>{this.state.selectedFile?.name}</div>
                            <Button onClick={() => window.open(this.state.selectedFile?.url ?? '', '_blank')} label='Download' icon={require('./assets/download.svg').default} type='secondary' />
                        </div>
                        <div style={styles.fileViewer}>
                            {this.renderFileViewer()}
                        </div>
                    </div>
                )}
            </div>
        );
    }
};

const styles = {
    page: {
        display: 'flex',
        flexGrow: 1,
        height: 'calc(100vh - 140px)',
        backgroundColor: Theme.Colours.background,
    },

    leftPanel: {
        display: 'flex',
        flexDirection: 'column' as const,
        width: '30%',
        position: 'relative' as const,
        margin: '52px 34px 34px 34px', // top, right, bottom, left
        border: Theme.Colours.border,
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: Theme.Colours.workArea,
    },

    contentPanel: {
        flexGrow: 1,
        // padding: '20px',
    },
    filePathContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        marginRight: '34px',
    },
    filePath: {
        ...Theme.Fonts.title2,
        color: Theme.Colours.text,
        flexGrow: 1,
    },
    downloadButton: {
        padding: '8px 16px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        border: Theme.Colours.border,
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        marginLeft: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    downloadIcon: {
        width: '16px',
        height: '16px',
    },
    fileViewer: {
        flexGrow: 1,
        margin: '12px 34px 34px 0px', // top, right, bottom, left
        border: Theme.Colours.border,
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: Theme.Colours.workArea,
    },
};
